import { defaultGroupPos } from './layout';
import type { OrgChartDoc, Person, LabelMode, Selection } from './types';

export interface OrgChartState {
  doc: OrgChartDoc | null;
  selection: Selection;
  editMode: boolean;
  zoom: 'fit' | '0.5' | '1';
  undoStack: OrgChartDoc[];
  canUndo: boolean;
  lastMutationAt: number | null;
  error: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface AnyAction { type: string; now?: number; [key: string]: any }

function loadedDoc(state: OrgChartState): OrgChartDoc {
  return state.doc as OrgChartDoc;
}

const LABEL_MODES: LabelMode[] = ['auto', 'radial', 'tangent', 'above', 'below', 'hidden'];
const CLAMP_RANGES = { sun: [40, 260] as [number, number], gdisc: [30, 340] as [number, number], pdisc: [22, 260] as [number, number] };
const UNDO_STACK_CAP = 80;
const UNDO_THROTTLE_MS = 700;
const THROTTLED_TYPES = new Set(['SET_PERSON_ANGLE', 'SET_DISC_POSITION', 'SET_SIZE']);

function clamp(v: number, lo: number, hi: number): number { return Math.max(lo, Math.min(hi, v)); }
function cloneDoc(doc: OrgChartDoc): OrgChartDoc { return JSON.parse(JSON.stringify(doc)); }
function deepClone<T>(value: T): T { return JSON.parse(JSON.stringify(value)); }

function applyLeadExclusivity(people: Person[], keepKi: number, lead?: true): void {
  if (!lead) return;
  people.forEach((person, i) => { if (i !== keepKi) delete person.lead; });
}

function repairSelection(doc: OrgChartDoc, selection: Selection): Selection {
  if (!selection) return null;
  if (selection.kind === 'sun') return selection;
  const group = doc.groups[selection.gi];
  if (!group) return null;
  if (selection.kind === 'group') return selection;
  const program = group.programs[selection.pi];
  if (!program) return null;
  if (selection.kind === 'prog') return selection;
  return program.people[selection.ki] ? selection : null;
}

function pushUndo(state: OrgChartState, action: AnyAction, nextDoc: OrgChartDoc): OrgChartState {
  const stack = state.undoStack;
  const lastMutationAt = state.lastMutationAt;
  const now = typeof action.now === 'number' ? action.now : null;
  const throttled = THROTTLED_TYPES.has(action.type) && now != null;
  const withinBurst = throttled && lastMutationAt != null && now != null && (now - lastMutationAt) <= UNDO_THROTTLE_MS;
  let undoStack = stack;
  if (!withinBurst) {
    undoStack = [...stack, loadedDoc(state)];
    if (undoStack.length > UNDO_STACK_CAP) undoStack.shift();
  }
  return { ...state, doc: nextDoc, undoStack, canUndo: undoStack.length > 0, lastMutationAt: throttled ? now : state.lastMutationAt, error: null };
}

function commit(state: OrgChartState, action: AnyAction, doc: OrgChartDoc, selection: Selection): OrgChartState {
  return { ...pushUndo(state, action, doc), selection };
}

export function orgChartReducer(state: OrgChartState, action: AnyAction): OrgChartState {
  switch (action.type) {
    case 'SET_EDIT_MODE':
      return { ...state, editMode: action.on as boolean, selection: action.on ? state.selection : null };
    case 'SELECT':
      return { ...state, selection: action.selection as Selection };
    case 'DESELECT':
      return { ...state, selection: null };
    case 'SET_ZOOM':
      return { ...state, zoom: action.zoom as OrgChartState['zoom'] };
    case 'SET_PERSON_ANGLE': {
      const doc = cloneDoc(loadedDoc(state));
      doc.groups[action.gi as number].programs[action.pi as number].people[action.ki as number].ang = Math.round((action.ang as number) * 10) / 10;
      return commit(state, action, doc, state.selection);
    }
    case 'REASSIGN_PERSON': {
      const doc = cloneDoc(loadedDoc(state));
      const src = doc.groups[action.gi as number].programs[action.pi as number];
      const person = src.people[action.ki as number];
      person.ang = Math.round((action.ang as number) * 10) / 10;
      src.people.splice(action.ki as number, 1);
      const dst = doc.groups[action.targetGi as number].programs[action.targetPi as number];
      dst.people.push(person);
      const newKi = dst.people.length - 1;
      applyLeadExclusivity(dst.people, newKi, person.lead);
      return commit(state, action, doc, { kind: 'person', gi: action.targetGi as number, pi: action.targetPi as number, ki: newKi });
    }
    case 'SET_DISC_POSITION': {
      const doc = cloneDoc(loadedDoc(state));
      if (action.kind === 'sun') { doc.sun.x = action.x as number; doc.sun.y = action.y as number; }
      else if (action.kind === 'group') { doc.groups[action.gi as number].x = action.x as number; doc.groups[action.gi as number].y = action.y as number; }
      else { doc.groups[action.gi as number].programs[action.pi as number].pang = action.pang as number; doc.groups[action.gi as number].programs[action.pi as number].pR = action.pR as number; }
      return commit(state, action, doc, state.selection);
    }
    case 'SET_SIZE': {
      const doc = cloneDoc(loadedDoc(state));
      const { target, gi, pi, value } = action as { type: string; target: string; gi?: number; pi?: number; value: number; min?: number; max?: number };
      if (target === 'sun') doc.settings.sunR = clamp(value, ...CLAMP_RANGES.sun);
      else if (target === 'gdisc') doc.groups[gi!].discR = clamp(value, ...CLAMP_RANGES.gdisc);
      else if (target === 'gring') doc.groups[gi!].ringR = clamp(value, (action.min as number) ?? 40, (action.max as number) ?? 520);
      else if (target === 'pdisc') doc.groups[gi!].programs[pi!].discR = clamp(value, ...CLAMP_RANGES.pdisc);
      else if (target === 'pring') doc.groups[gi!].programs[pi!].ringR = clamp(value, (action.min as number) ?? 34, (action.max as number) ?? 460);
      return commit(state, action, doc, state.selection);
    }
    case 'CYCLE_LABEL_MODE': {
      const doc = cloneDoc(loadedDoc(state));
      const person = doc.groups[action.gi as number].programs[action.pi as number].people[action.ki as number];
      const cur = (person.labelMode || doc.settings.labelMode) as LabelMode;
      person.labelMode = LABEL_MODES[(LABEL_MODES.indexOf(cur) + 1) % LABEL_MODES.length];
      return commit(state, action, doc, state.selection);
    }
    case 'ADD_PERSON': {
      const doc = cloneDoc(loadedDoc(state));
      const people = doc.groups[action.gi as number].programs[action.pi as number].people;
      people.push(action.person as Person);
      const ki = people.length - 1;
      applyLeadExclusivity(people, ki, (action.person as Person).lead);
      return commit(state, action, doc, { kind: 'person', gi: action.gi as number, pi: action.pi as number, ki });
    }
    case 'UPDATE_PERSON': {
      const doc = cloneDoc(loadedDoc(state));
      const targetGi = (action.targetGi as number) ?? (action.gi as number);
      const targetPi = (action.targetPi as number) ?? (action.pi as number);
      const sourceProgram = doc.groups[action.gi as number].programs[action.pi as number];
      const person = action.patch as Person;
      let ki: number;
      if (targetGi === (action.gi as number) && targetPi === (action.pi as number)) {
        sourceProgram.people[action.ki as number] = person;
        ki = action.ki as number;
      } else {
        sourceProgram.people.splice(action.ki as number, 1);
        const targetProgram = doc.groups[targetGi].programs[targetPi];
        targetProgram.people.push(person);
        ki = targetProgram.people.length - 1;
      }
      applyLeadExclusivity(doc.groups[targetGi].programs[targetPi].people, ki, person.lead);
      return commit(state, action, doc, { kind: 'person', gi: targetGi, pi: targetPi, ki });
    }
    case 'DELETE_PERSON': {
      const doc = cloneDoc(loadedDoc(state));
      doc.groups[action.gi as number].programs[action.pi as number].people.splice(action.ki as number, 1);
      return commit(state, action, doc, repairSelection(doc, state.selection));
    }
    case 'DUPLICATE_PERSON': {
      const doc = cloneDoc(loadedDoc(state));
      const people = doc.groups[action.gi as number].programs[action.pi as number].people;
      const copy = deepClone(people[action.ki as number]);
      delete copy.lead;
      if (!copy.vacant) copy.name = `${copy.name} (copy)`;
      if (typeof copy.ang === 'number') copy.ang += 14;
      people.push(copy);
      return commit(state, action, doc, { kind: 'person', gi: action.gi as number, pi: action.pi as number, ki: people.length - 1 });
    }
    case 'ADD_PROGRAM': {
      const doc = cloneDoc(loadedDoc(state));
      const programs = doc.groups[action.gi as number].programs;
      programs.push(action.program);
      return commit(state, action, doc, { kind: 'prog', gi: action.gi as number, pi: programs.length - 1 });
    }
    case 'UPDATE_PROGRAM': {
      const targetGi = (action.targetGi as number) ?? (action.gi as number);
      if (targetGi !== (action.gi as number) && loadedDoc(state).groups[action.gi as number].programs.length === 1) {
        return { ...state, error: 'A group needs at least one program.' };
      }
      const doc = cloneDoc(loadedDoc(state));
      const sourceGroup = doc.groups[action.gi as number];
      const original = sourceGroup.programs[action.pi as number];
      const updated = { ...original, ...(action.patch as object), people: original.people };
      let pi: number;
      if (targetGi === (action.gi as number)) { sourceGroup.programs[action.pi as number] = updated; pi = action.pi as number; }
      else { sourceGroup.programs.splice(action.pi as number, 1); const tg = doc.groups[targetGi]; tg.programs.push(updated); pi = tg.programs.length - 1; }
      return commit(state, action, doc, { kind: 'prog', gi: targetGi, pi });
    }
    case 'DELETE_PROGRAM': {
      if (loadedDoc(state).groups[action.gi as number].programs.length === 1) {
        return { ...state, error: 'A group needs at least one program — delete the group instead.' };
      }
      const doc = cloneDoc(loadedDoc(state));
      doc.groups[action.gi as number].programs.splice(action.pi as number, 1);
      return commit(state, action, doc, repairSelection(doc, state.selection));
    }
    case 'DUPLICATE_PROGRAM': {
      const doc = cloneDoc(loadedDoc(state));
      const programs = doc.groups[action.gi as number].programs;
      const copy = deepClone(programs[action.pi as number]);
      copy.id = `${copy.id}-2`; copy.short = `${copy.short} (copy)`;
      if (typeof copy.pang === 'number') copy.pang += 25;
      programs.push(copy);
      return commit(state, action, doc, { kind: 'prog', gi: action.gi as number, pi: programs.length - 1 });
    }
    case 'ADD_GROUP': {
      const doc = cloneDoc(loadedDoc(state));
      const ag = action.group as { id: string; short: string; label: string; [k: string]: unknown };
      const group = { ...ag, programs: [{ id: ag.id, short: ag.short, label: ag.label, people: [] as Person[] }] };
      doc.groups.push(group as unknown as OrgChartDoc['groups'][number]);
      return commit(state, action, doc, { kind: 'group', gi: doc.groups.length - 1 });
    }
    case 'UPDATE_GROUP': {
      const doc = cloneDoc(loadedDoc(state));
      const original = doc.groups[action.gi as number];
      doc.groups[action.gi as number] = { ...original, ...(action.patch as object), programs: original.programs };
      return commit(state, action, doc, { kind: 'group', gi: action.gi as number });
    }
    case 'DELETE_GROUP': {
      const doc = cloneDoc(loadedDoc(state));
      doc.groups.splice(action.gi as number, 1);
      return commit(state, action, doc, repairSelection(doc, state.selection));
    }
    case 'DUPLICATE_GROUP': {
      const doc = cloneDoc(loadedDoc(state));
      const original = doc.groups[action.gi as number];
      const copy = deepClone(original);
      copy.id = `${copy.id}-2`; copy.short = `${copy.short} (copy)`;
      const resolved = typeof original.x === 'number' && typeof original.y === 'number'
        ? { x: original.x, y: original.y } : defaultGroupPos(action.gi as number, doc.groups.length, doc.sun.x, doc.sun.y);
      copy.x = resolved.x + 160; copy.y = resolved.y + 160;
      doc.groups.push(copy);
      return commit(state, action, doc, { kind: 'group', gi: doc.groups.length - 1 });
    }
    case 'UPDATE_LEADERSHIP': {
      const doc = cloneDoc(loadedDoc(state));
      if (action.code != null) doc.org.code = action.code as string;
      Object.assign(doc.org.pm, action.pm || {});
      Object.assign(doc.org.dpm, action.dpm || {});
      if (action.sunR != null) doc.settings.sunR = action.sunR as number;
      if (action.sunX != null) doc.sun.x = action.sunX as number;
      if (action.sunY != null) doc.sun.y = action.sunY as number;
      return commit(state, action, doc, state.selection);
    }
    case 'UPDATE_SETTINGS': {
      const doc = cloneDoc(loadedDoc(state));
      Object.assign(doc.settings, action.patch);
      return commit(state, action, doc, state.selection);
    }
    case 'HYDRATE_DOC': {
      const doc = cloneDoc(action.doc as OrgChartDoc);
      return { ...state, doc, selection: null, undoStack: [], canUndo: false, lastMutationAt: null, error: null };
    }
    case 'AUTO_ARRANGE': {
      const doc = cloneDoc(loadedDoc(state));
      doc.groups.forEach(group => {
        delete group.x; delete group.y; delete group.discR; delete group.ringR;
        group.programs.forEach(program => {
          delete program.pang; delete program.pR; delete program.discR; delete program.ringR;
          program.people.forEach(person => { delete person.ang; });
        });
      });
      return commit(state, action, doc, state.selection);
    }
    case 'RESET_TO_BASELINE': {
      const doc = cloneDoc(action.baseline as OrgChartDoc);
      return commit(state, action, doc, null);
    }
    case 'IMPORT_DOC': {
      const doc = cloneDoc(action.doc as OrgChartDoc);
      return commit(state, action, doc, repairSelection(doc, state.selection));
    }
    case 'UNDO': {
      const stack = state.undoStack;
      if (stack.length === 0) return state;
      const previous = stack[stack.length - 1];
      const undoStack = stack.slice(0, -1);
      return { ...state, doc: previous, selection: null, undoStack, canUndo: undoStack.length > 0 };
    }
    default:
      return state;
  }
}
